import { useCallback, useEffect, useState } from "react";
import * as React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router"
import "../../../Styles/main.scss"
import Button from "../../Shared/Primitive/Button"
import TextInput from "../../Shared/Primitive/TextInput"
import { useSelector, useDispatch } from "react-redux"
import { RootState } from "../../../App/store"
import { signinPostRequest } from "./SigninServiceRequests"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { resetSigninError } from "./SigninSlice"
import HTTPService from "../../../App/HTTPService"
import { setModalData } from "../../Shared/SharedSlices/ModalSlice"
import SessionService from "../../../App/SessionService"
import { StorageKey } from "../../../App/enums"

const SignInForm = () => {
    const translation = useSelector(selectTranslations)
    const { token, newEmailToken } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()
    const errorState = useSelector((state: RootState) => state.signinSlice.signinError)
    const organizationerrorState = useSelector((state: RootState) => state.EdusiiaMatchingSlice.addOrganizationError)
    const authenticationState = useSelector((state: RootState) => state.signinSlice.authenticationSlice)
    const [signinForm, setSigninForm] = useState({ email: "", password: "" })
    let params = new URLSearchParams(location.search)
    const linkName = "invitation-link"
    const redirectUrl = params.get("redirectUrl")
    const storedRedirectUrl = redirectUrl || SessionService.getItem(StorageKey.redirectUrl)
    const previewRoomJoinId = SessionService.getItem(StorageKey.previewRoomJoinId)
    const roomInvitationIdFromRedirect = redirectUrl?.split("/")[2]
    const previewRedirectUrl = previewRoomJoinId ? `/room/${previewRoomJoinId}?previewJoin=1` : null

    useEffect(() => {
        const finalRedirectUrl = storedRedirectUrl || previewRedirectUrl
        if (!redirectUrl && finalRedirectUrl) {
            navigate(`/signin?redirectUrl=${finalRedirectUrl}`, { replace: true })
            return
        }

        if (redirectUrl?.split("/")[1] === linkName) {
            SessionService.setItem(StorageKey.roomInvitationId, roomInvitationIdFromRedirect as string)
        }
    }, [navigate, previewRedirectUrl, redirectUrl, roomInvitationIdFromRedirect, storedRedirectUrl])

    useEffect(() => {
        if (authenticationState?.hasAccessToken) {
            // User successfully signed in, navigate to redirectUrl
            const finalUrl = redirectUrl || storedRedirectUrl || previewRedirectUrl || "/"
            // Cleanup
            SessionService.removeItem(StorageKey.redirectUrl)
            SessionService.removeItem(StorageKey.previewRoomJoinId)
            navigate(finalUrl, { replace: true })
        }
    }, [authenticationState?.hasAccessToken, navigate, redirectUrl, storedRedirectUrl, previewRedirectUrl])

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSigninForm({ ...signinForm, [event.target.name]: event.target.value })
    }

    const onFormSubmit = (event: { preventDefault: () => void }) => {
        event.preventDefault()
        dispatch<any>(signinPostRequest(signinForm))
    }

    const onSignupButtonClick = () => {
        const targetRedirectUrl = storedRedirectUrl || previewRedirectUrl
        navigate(targetRedirectUrl ? `/signup?redirectUrl=${targetRedirectUrl}` : "/signup")
        dispatch(resetSigninError())
    }

    const getEmailVerification = useCallback(() => {
        return HTTPService.get(`/email-verify/?token=${token}`)
    }, [token])

    const getNewEmailVerification = useCallback(() => {
        return HTTPService.get(`/email-verify/?token=${token}&newemail=${newEmailToken}`)
    }, [newEmailToken, token])

    useEffect(() => {
        if (token) {
            getEmailVerification()
                .then(response => {
                    if (response.status === 200) {
                        dispatch(setModalData({ open: true, postiveEmailVerification: true }))
                    }
                })
                .catch(error => {
                    dispatch(setModalData({ open: true, negativeEmailVerification: { status: true, message: error.response.data.error } }))
                })
        }
        if (newEmailToken) {
            getNewEmailVerification()
                .then(response => {
                    if (response.status === 200) {
                        dispatch(setModalData({ open: true, postiveNewEmailVerification: true }))
                    }
                })
                .catch(error => {
                    dispatch(setModalData({ open: true, negativeEmailVerification: { status: true, message: error.response.data.error } }))
                })
        }
    }, [dispatch, getEmailVerification, getNewEmailVerification, newEmailToken, token])

    return (
        <>
            <form className="signin-form" onSubmit={onFormSubmit}>
                <div className="signin-form__description">
                    <p> {translation.weConnectOrganizations}</p>
                    <p> {translation.andPeopleWhoWantToMake}</p>
                    <p> {translation.educationBetter} - {translation.totallyFree}!</p>
                </div>

                <div className="signin-form__title signin-form__title--margin-top-8 ">
                    <h2 className="h2">{translation.signin}</h2>
                </div>
                <div className="signin-form signin-form__single-input">
                    <p className="p3 signin-form__small-text ">{translation.email}</p>
                    <TextInput type="text" textinputsize="large" name="email" value={signinForm.email} onChange={onInputChange} />
                    <p className="p3  signin-form__error-message">{errorState.message.email}</p>
                </div>

                <div className="signin-form signin-form__single-input">
                    <p className="p3 signin-form__small-text ">{translation.securePassword}</p>
                    <TextInput type="password" textinputsize="large" name="password" value={signinForm.password} autoComplete="on" onChange={onInputChange} />
                    <p className="p3 signin-form__error-message">
                        {errorState.message.password} {errorState.message.non_field_errors}
                    </p>
                </div>
                <div className=" signin-form__forgot-password">
                    <Link to="/forgotpassword" className="p2 signin-form__forgot-password">
                        {translation.forgotPassword}?
                    </Link>

                    <p className="p3  signin-form__error-message">{organizationerrorState.message.error}</p>
                </div>
                <div className=" signin-form__button-container">
                    <div className=" signin-form__button-size">
                        <Button
                            type="submit"
                            className="button button--border-radius-09 button--color-red button--hover-brighter button--cursor-pointer button--text-color-white button--font-size-large button--font-weight-400  "
                        >
                            <div>{translation.signin}</div>
                        </Button>

                    </div>
                    <div className=" signin-form__button-size">
                        <Button
                            onClick={onSignupButtonClick}
                            type="button"
                            className="button button--border-radius-09 button--border-red button--color-white button--hover-brighter button--cursor-pointer button--text-color-red button--font-size-large button--font-weight-400  ">
                            <div>{translation.signup}</div>
                        </Button>
                    </div>
                </div>
            </form>
        </>
    )
}
export default SignInForm
